import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * "How many times should this repeat?" for the booking CREATION flow.
 *
 * Creating a recurring booking previously had no way to choose a length: the series was created
 * indefinite while the confirmation screen previewed a hardcoded 12 occurrences, so the preview and
 * the reality disagreed and the operator had no control over either. Editing an existing booking
 * already let you set a count — this brings creation into line.
 *
 * `undefined` means "no end date (ongoing)", matching how the rest of the system represents an
 * indefinite series (a stored recurrence with no `count`).
 */

export const MIN_OCCURRENCES = 2;
/** One year of weekly occurrences. Also the slot default, so the two flows agree. */
export const MAX_OCCURRENCES = 52;

interface OccurrenceCountPickerProps {
  /** undefined = ongoing. */
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  /** Preview count used when the series is ongoing, so the caller can label the list honestly. */
  previewWhenOngoing: number;
  disabled?: boolean;
}

export const OccurrenceCountPicker = ({ value, onChange, previewWhenOngoing, disabled }: OccurrenceCountPickerProps) => {
  const isOngoing = value === undefined;

  return (
    <div className="flex flex-col gap-2" data-testid="occurrence-count-picker">
      <Label className="text-sm font-medium">How many times should this repeat?</Label>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          name="occurrence-mode"
          data-testid="occurrence-mode-ongoing"
          checked={isOngoing}
          onChange={() => onChange(undefined)}
          disabled={disabled}
          className="mt-1"
        />
        <span>
          <strong>No end date</strong> — repeats until cancelled.{" "}
          <span className="text-gray-600">Showing the next {previewWhenOngoing} below.</span>
        </span>
      </label>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="radio"
          name="occurrence-mode"
          data-testid="occurrence-mode-fixed"
          checked={!isOngoing}
          // Default to a sensible finite length rather than 1, which is not really a series.
          onChange={() => onChange(previewWhenOngoing)}
          disabled={disabled}
          className="mt-1"
        />
        <span>
          <strong>A set number of times</strong>
        </span>
      </label>

      {!isOngoing && (
        <div className="ml-6 flex items-center gap-2">
          <Input
            id="occurrence-count"
            type="number"
            inputMode="numeric"
            min={MIN_OCCURRENCES}
            max={MAX_OCCURRENCES}
            value={value}
            data-testid="occurrence-count-input"
            disabled={disabled}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              // Clamp rather than reject: a typo of 999 becomes 52 instead of silently creating a
              // decade-long series, and an unparseable value leaves the current count alone.
              if (Number.isNaN(parsed)) return;
              onChange(Math.min(MAX_OCCURRENCES, Math.max(MIN_OCCURRENCES, parsed)));
            }}
            className="w-24 bg-white"
            aria-label="Number of occurrences"
          />
          <span className="text-sm text-gray-600">occurrences (max {MAX_OCCURRENCES})</span>
        </div>
      )}
    </div>
  );
};

export default OccurrenceCountPicker;
